'use client';
import React from 'react';

import { Box } from '@mui/material';
import Grid from '@mui/material/Grid';
import { useTranslations } from 'next-intl';
import { useLocale } from 'next-intl';
import styled from 'styled-components';

import ContentHeading from './ContentHeading';
import ContentLanguageSwitcher from './ContentLanguageSwitcher';
import RelativeDate from './RelativeDate';
import RelevantFor from './RelevantFor';

const Wrapper = styled.div`
  margin-bottom: 50px;
  padding-bottom: 40px;
  position: relative;

  &:after {
    background: ${props => props.theme.palette.primary.main};
    bottom: -3px;
    content: '';
    display: block;
    height: 3px;
    left: -45px;
    position: absolute;
    right: -45px;

    ${({ theme }) => theme.breakpoints.down(1390)} {
      left: -20px;
      right: -20px;
    }
  }
`;

const ContentHeadingWrapper = styled.div`
  /* max-width: 730px; */

  ${({ theme }) => theme.breakpoints.down('md')} {
    max-width: none;
  }
`;

const Dot = styled.span`
  padding: 0 0.5rem;
`;

const ScrollToComments = styled.a`
  cursor: pointer;
  text-decoration: underline;
  color: #000;

  &:hover {
    color: ${({ theme }) => theme.custom.colors.brand};
  }
`;

const SmallGrayText = styled.span`
  color: #000;
  font-family: 'Atlas Grotesk';
  font-size: 0.75rem;
  font-style: normal;
  font-weight: 300;
  line-height: normal;
`;

const messages = {
  en: {
    one: '{count} comment',
    other: '{count} comments',
  },
  de: {
    one: '{count} Kommentar',
    other: '{count} Kommentare',
  },
  fr: {
    one: '{count} commentaire',
    other: '{count} commentaires',
  },
  es: {
    one: '{count} comentario',
    other: '{count} comentarios',
  },
  it: {
    one: '{count} commento',
    other: '{count} commenti',
  },
  ja: {
    one: '{count} コメント',
    other: '{count} コメント',
  },
  pl: {
    one: '{count} komentarz',
    few: '{count} komentarze',
    many: '{count} komentarzy',
    other: '{count} komentarzy',
  },
  cs: {
    one: '{count} komentář',
    few: '{count} komentáře',
    many: '{count} komentářů',
    other: '{count} komentářů',
  },
};

const CommentsCount = ({ count }) => {
  const locale = useLocale();
  const pluralRule = new Intl.PluralRules(locale).select(count);

  if (!count) {
    return null;
  }

  return <span>{messages[locale][pluralRule].replace('{count}', count)}</span>;
};

interface ContentHeaderProps {
  title: string;
  tags?: string[];
  date?: string;
  comments?: number;
  translations?: {
    [key: string]: string;
  };
  products?: string[];
  articleProducts?: string[];
  allProducts?: Product[];
  translationsBasePath?: string;
  translationsType?: string;
  simple?: boolean;
}

export default function ContentHeader({
  title,
  tags,
  date,
  comments = 0,
  translations,
  articleProducts,
  allProducts,
  translationsBasePath,
  translationsType,
  simple,
}: ContentHeaderProps) {
  const showMetadata = !!(
    tags ||
    articleProducts ||
    allProducts ||
    date ||
    translations ||
    translationsBasePath ||
    translationsType
  );

  const translate = useTranslations();

  return (
    <Wrapper>
      <ContentHeadingWrapper>
        <ContentHeading text={title} simple={simple} />
      </ContentHeadingWrapper>

      {showMetadata && (
        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <Box>
              <RelevantFor articleProducts={articleProducts} allProducts={allProducts} />
              <Box
                sx={{
                  mt: { xs: 1, lg: 2 },
                }}
              >
                <SmallGrayText>
                  <RelativeDate date={date} />
                  {comments > 0 && (
                    <>
                      <Dot>•</Dot>
                      <ScrollToComments href="#comments-section">
                        <CommentsCount count={comments} />
                      </ScrollToComments>
                    </>
                  )}
                </SmallGrayText>
              </Box>
            </Box>
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
            sx={theme => ({
              [theme.breakpoints.down('md')]: {
                paddingTop: '30px !important',
              },
            })}
          >
            <Box display="flex" alignItems="center" marginLeft="auto">
              <ContentLanguageSwitcher
                translations={translations}
                basePath={translationsBasePath}
                contentType={translate(`${translationsType}-contentType`)}
              />
            </Box>
          </Grid>
        </Grid>
      )}
    </Wrapper>
  );
}
